import { atom } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';
import { isEqual } from 'lodash-es';
import request from 'lib/request';
import type { EvaluationCaseDetail as Eval3EvaluationCaseDetail } from '../eval3-new/types';
import {
  atomWithLoadable,
  lazyAtom,
  mutationAtomWithLoadable,
} from 'lib/atoms';
import type {
  DateFilterType,
  FreeCashFlowAggregationPeriod,
  MeasurementType,
  SlotVolumeSource,
  SlotAfeSource,
} from 'types/evaluation';
import type { PackageEvaluationsResponse } from 'types/evaluation-api/responses';
import type {
  AggregatedFreeCashFlowResultResponse,
  ExecutiveSummary,
} from 'types/api-responses';
import type {
  SlotVolumeSourceConfiguration,
  SlotAfeSourceConfiguration,
  SlotCostDateConfiguration,
} from '../evaluation-result/aggregated-free-cash-flow/types';
import type {
  CalculateRequest,
  CalculateResponse,
  CalculationConfig,
  CalculationDetail,
  CalculationsResponse,
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  DevelopmentPaceScenario,
  DiscountRateScenarioItem,
  DpsScheduleEnqueueResponse,
  DpsScheduleResponse,
  Eval2Run,
  EvaluationCalculation,
  RenameWorkspaceRequest,
  RenameWorkspaceResponse,
  SaveRequest,
  SaveResponse,
  SlotDateOverride,
  WorkspacesResponse,
  DpsScheduleStatus,
  DcaScenarioItem,
  TaxScenarioItem,
  ExpenseScenarioListItem,
  EvalExportPresets,
} from './types';
import type {
  AriesMdbKickoffResponse,
  AriesMdbExportConfig,
  AriesMdbExportDefaults,
  AriesRestoredRun,
} from 'components/aries-mdb-export/types';
import type { WorkingCopyValues } from './util';

// Re-export types that were moved to types.ts for consumers importing from atoms.
export type { DpsScheduleStatus, DcaScenarioItem } from './types';

// ---------------------------------------------------------------------------
// Notifications — centralized error notifications for API failures.
// ---------------------------------------------------------------------------

/**
 * `source` is an optional logical key identifying the workflow that produced
 * the notification (e.g. `'dps-schedule'`, `'save'`). When set, pushing a new
 * notification with the same source replaces any existing entries from that
 * source, and the workflow's success path can clear all entries from its
 * source via `clearNotificationsBySource`. Notifications without a source
 * remain pure-append (legacy behavior).
 */
export interface AppNotification {
  id: string;
  message: string;
  source?: string;
}

export const notificationsAtom = atomFamily((_pkgId: number) =>
  atom<AppNotification[]>([])
);

// ---------------------------------------------------------------------------
// Selection atoms — track which workspace, calculation, and Eval 2.0 run the
// user currently has selected. Keyed by packageId, in-memory only.
// ---------------------------------------------------------------------------

// The currently selected workspace ID.
export const selectedWorkspaceIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// The currently selected (or most recently created) calculation ID.
export const selectedCalculationIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// The Eval 2.0 run ID chosen from the dropdown in GlobalSettings.
// Retained for legacy hydration (pre-INS-8827 calcs hydrated from
// `detail.eval2EvaluationId`) and any read-only display surfaces. New
// calcs no longer require it — selection now flows through
// `selectedScenarioIdAtom`.
export const selectedEval2IdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// The Scenario ID chosen from the scenario picker in GlobalSettings.
// Sent as `scenario_id` on POST /evaluations/calculate (required) and on
// POST /packages/:pkg/development_pace_scenarios/:dps/schedule (BE looks
// up interest_type server-side from this).
export const selectedScenarioIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// The selected DPS ID chosen from the dropdown.
export const selectedDpsIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// The DPS schedule result (blob paths + slot count) from calling the schedule endpoint.
// Set after user picks a DPS from the dropdown and scheduling completes.
export const dpsScheduleResultAtom = atomFamily((_id: number) =>
  atom<DpsScheduleResponse | null>(null)
);

export const dpsScheduleStatusAtom = atomFamily((_id: number) =>
  atom<DpsScheduleStatus>('idle')
);

// Whether a DPS schedule request is in progress (derived for backward compat).
export const isDpsSchedulingAtom = atomFamily((_id: number) =>
  atom((get) => {
    const status = get(dpsScheduleStatusAtom(_id));
    return status === 'scheduling' || status === 'caching';
  })
);

// The Price Deck ID chosen from the dropdown in GlobalSettings.
export const selectedPriceDeckIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// Controls visibility of the price deck detail slide-out panel.
export const priceDeckDetailPanelOpenAtom = atom(false);

// Fetches active price decks for the dropdown. Triggered on mount with the
// currently selected deck ID (for include_id support).
export interface PriceDeckListItem {
  id: number;
  name: string;
}
export const [fetchPriceDecksAtom, fetchPriceDecksLoadableAtom] =
  mutationAtomWithLoadable<PriceDeckListItem[], [number | null]>(
    async (_get, _set, includeId: number | null) => {
      const controller = new AbortController();
      const res = await request.post<{ results: PriceDeckListItem[] }, unknown>(
        '/queries/price_decks',
        {
          query: {
            status: ['active'],
            sortAttribute: 'updated_at',
            sortDirection: 'DESC',
            pageSize: 500,
            preferModifiedStrip: true,
            ...(includeId ? { includeId } : {}),
          },
        },
        controller
      );
      if (res.type === 'success') return res.data.results ?? [];
      throw new Error('Failed to fetch price decks');
    }
  );

// The DCA Scenario ID chosen from the dropdown in GlobalSettings.
// null means "Legacy" (use Legacy PDP forecasts only).
export const selectedDcaScenarioIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// DCA Scenario list for the dropdown.
export const [fetchDcaScenariosAtom, fetchDcaScenariosLoadableAtom] =
  mutationAtomWithLoadable<DcaScenarioItem[], []>(async () => {
    const response = await request.get<DcaScenarioItem[]>('/dca_scenarios');
    if ('error' in response) {
      throw new Error(
        (response as { error: string }).error ?? 'Failed to fetch DCA scenarios'
      );
    }
    return response as DcaScenarioItem[];
  });

// Snapshot of the config that was last sent to the server for calculation.
// Used by isDirtyAtom to detect whether the user has changed settings
// since the last calculation.
export const appliedConfigAtom = atomFamily((_id: number) =>
  atom<CalculationConfig | null>(null)
);

// Set when a calculation result is ready to be fetched and displayed.
// Stores the calculation DB id + UUID when the result is ready to fetch.
// The `id` is the numeric DB id used by POST /evaluations/calculations/:id/eval_result.
// The `calculationId` is the UUID used by the eval_calculator / cashflows-by-month API.
export const calculationResultReadyCalcIdAtom = atomFamily((_id: number) =>
  atom<{ id: number; calculationId: string } | null>(null)
);

// ---------------------------------------------------------------------------
// Working copy atoms — the current settings the user is editing. Hydrated
// from calculation detail on tile click, or initialized with defaults on
// zero-sum. Keyed by packageId, in-memory only (NOT localStorage).
// ---------------------------------------------------------------------------

// The effective date currently set in the settings panel.
// Defaults to today in YYYY-MM-DD server format.
export const workingEffectiveDateAtom = atomFamily((_id: number) =>
  atom<string | null>(new Date().toISOString().slice(0, 10))
);

// The time interval grouping currently set in the settings panel.
export const workingIntervalAtom = atomFamily((_id: number) =>
  atom<FreeCashFlowAggregationPeriod>('year')
);

// The measurement type (gross/net) currently set in the settings panel.
export const workingMeasurementTypeAtom = atomFamily((_id: number) =>
  atom<MeasurementType>('net')
);

// The default slot volume source currently set in the settings panel.
export const workingSlotVolumeSourceAtom = atomFamily((_id: number) =>
  atom<SlotVolumeSource>('eval_3_0')
);

// The global AFE source setting (afe = smart defaults, scenario = scenario costs).
export const workingSlotAfeSourceAtom = atomFamily((_id: number) =>
  atom<SlotAfeSource>('afe')
);

// Per-slot volume source overrides currently set by the user.
// Maps slot ID to its volume source configuration.
export const workingSlotVolumeConfigurationsAtom = atomFamily((_id: number) =>
  atom<Record<string, SlotVolumeSourceConfiguration>>({})
);

// Per-slot AFE source overrides currently set by the user.
// Maps slot ID to its AFE source configuration.
export const workingSlotAfeConfigurationsAtom = atomFamily((_id: number) =>
  atom<Record<string, SlotAfeSourceConfiguration>>({})
);

// Per-slot cost date overrides currently set by the user.
// Maps slot ID to its cost date configuration.
export const workingSlotCostDateConfigurationsAtom = atomFamily((_id: number) =>
  atom<Record<string, SlotCostDateConfiguration>>({})
);

// Per-slot scheduled date overrides (permit, spud, first_production, cashflow).
// Layered on top of DPS scheduled dates from the blob.
export const workingSlotDateConfigurationsAtom = atomFamily((_id: number) =>
  atom<Record<string, SlotDateOverride>>({})
);

// Number of date intervals before the effective date (for relative date range).
export const workingIntervalsPreAtom = atomFamily((_id: number) =>
  atom<number>(2)
);

// Number of date intervals after the effective date (for relative date range).
export const workingIntervalsPostAtom = atomFamily((_id: number) =>
  atom<number>(10)
);

// Absolute minimum date for the date range filter.
export const workingAbsoluteMinDateAtom = atomFamily((_id: number) =>
  atom<string | null>(null)
);

// Absolute maximum date for the date range filter.
export const workingAbsoluteMaxDateAtom = atomFamily((_id: number) =>
  atom<string | null>(null)
);

// Whether to use relative or absolute date range filtering.
export const workingDateRangeFilterTypeAtom = atomFamily((_id: number) =>
  atom<DateFilterType>('relative')
);

// The createdAt timestamp from the most recently fetched calculation detail.
// Used by PriceDeckDetailPanel to show the price deck as-of calculation time.
export const calculationCreatedAtAtom = atomFamily((_id: number) =>
  atom<string | null>(null)
);

// ---------------------------------------------------------------------------
// GET atoms — atomFamily + lazyAtom (delayed first fetch, reactive to deps
// after trigger). All require an explicit first trigger via the setter.
// Consumer wraps with loadable() for loading/error states.
// Call the setter again to refetch.
// ---------------------------------------------------------------------------

// GET /packages/:id/evaluations — Eval 2.0 runs for dropdown.
/**
 * Lazy atom that fetches Eval 2.0 runs for a package.
 * Call the setter to trigger the initial fetch; stable during session.
 * Wrap with loadable() at the consumer for loading/error states.
 *
 * @param {number} pkgId - The package ID to fetch runs for.
 * @return {WritableAtom} Lazy atom returning Eval2Run[] or null.
 */
export const eval2RunsAtom = atomFamily((_pkgId: number) =>
  lazyAtom<Eval2Run[]>(async () => {
    const data = await request.get<PackageEvaluationsResponse[]>(
      `/packages/${_pkgId}/evaluations`
    );
    if ((data as { error?: string }).error) {
      throw new Error((data as { error: string }).error);
    }
    return (data as PackageEvaluationsResponse[]).map((r) => ({
      id: r.id!,
      scenarioId: r.scenario.id,
      scenario: r.scenario.name,
      scenarioHref: r.scenario.href,
      interestType: r.scenario.interestType,
      user: r.createdBy ?? 'Auto Generated',
      runDate: r.createdAt,
    }));
  })
);

// POST /queries/development_pace_scenarios — DPS list for dropdown.
/**
 * Mutation atom that fetches Development Pace Scenarios via the query endpoint.
 * DPS records are global (not package-scoped).
 * Uses mutationAtomWithLoadable so loadable() resolves correctly with POST.
 *
 * @return {[WritableAtom, WritableAtom]} Tuple of [baseAtom, loadableAtom].
 */
export const [fetchDpsListAtom, fetchDpsListLoadableAtom] =
  mutationAtomWithLoadable<DevelopmentPaceScenario[], []>(async () => {
    const controller = new AbortController();
    const res = await request.post<
      { results: DevelopmentPaceScenario[] },
      unknown
    >(
      '/queries/development_pace_scenarios',
      {
        query: {
          sort_attribute: 'updated_at',
          sort_direction: 'DESC',
          page_size: 500,
        },
      },
      controller
    );
    if (res.type === 'success') return res.data.results ?? [];
    throw new Error('Failed to fetch development pace scenarios');
  });

// The selected DRS ID chosen from the dropdown.
export const selectedDrsIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// The selected Differential Scenario ID chosen from the dropdown.
export const selectedDifferentialScenarioIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

// Fetches the list of Discount Rate Scenarios for the DRS dropdown.
export const [fetchDrsListAtom, fetchDrsListLoadableAtom] =
  mutationAtomWithLoadable<DiscountRateScenarioItem[], []>(async () => {
    const controller = new AbortController();
    const res = await request.post<
      { results: DiscountRateScenarioItem[] },
      unknown
    >(
      '/queries/discount_rate_scenarios',
      {
        query: {
          sort_attribute: 'updated_at',
          sort_direction: 'DESC',
          page_size: 500,
        },
      },
      controller
    );
    if (res.type === 'success') return res.data.results ?? [];
    throw new Error('Failed to fetch discount rate scenarios');
  });

// POST /queries/differential_scenarios — Differential Scenario list for dropdown.
export interface DifferentialScenarioItem {
  id: number;
  href: string;
  name: string;
  effectiveDate: string | null;
}

export const [
  fetchDifferentialScenarioListAtom,
  fetchDifferentialScenarioListLoadableAtom,
] = mutationAtomWithLoadable<DifferentialScenarioItem[], []>(async () => {
  const controller = new AbortController();
  const res = await request.post<
    { results: DifferentialScenarioItem[] },
    unknown
  >(
    '/queries/differential_scenarios',
    {
      query: {
        sort_attribute: 'updated_at',
        sort_direction: 'DESC',
        page_size: 500,
      },
    },
    controller
  );
  if (res.type === 'success') return res.data.results ?? [];
  throw new Error('Failed to fetch differential scenarios');
});

// ── Tax Scenario selection ──────────────────────────────────────────

export const selectedTaxScenarioIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

export const [fetchTaxScenarioListAtom, fetchTaxScenarioListLoadableAtom] =
  mutationAtomWithLoadable<TaxScenarioItem[], []>(async () => {
    const controller = new AbortController();
    const res = await request.post<{ results: TaxScenarioItem[] }, unknown>(
      '/queries/tax_scenarios',
      {
        query: {
          sort_attribute: 'updated_at',
          sort_direction: 'DESC',
          page_size: 500,
        },
      },
      controller
    );
    if (res.type === 'success') return res.data.results ?? [];
    throw new Error('Failed to fetch tax scenarios');
  });

// ── Expense Scenario selection ──────────────────────────────────────

export const selectedExpenseScenarioIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

export const [
  fetchExpenseScenarioListAtom,
  fetchExpenseScenarioListLoadableAtom,
] = mutationAtomWithLoadable<ExpenseScenarioListItem[], []>(async () => {
  const controller = new AbortController();
  const res = await request.post<
    { results: ExpenseScenarioListItem[] },
    unknown
  >(
    '/queries/expense_scenarios',
    {
      query: {
        sort_attribute: 'updated_at',
        sort_direction: 'DESC',
        page_size: 500,
      },
    },
    controller
  );
  if (res.type === 'success') return res.data.results ?? [];
  throw new Error('Failed to fetch expense scenarios');
});

// GET /packages/:id/evaluation_workspaces — List workspaces.
/**
 * Lazy atom that fetches workspaces for a package.
 * Call the setter to trigger the initial fetch; call again to refetch.
 * Wrap with loadable() at the consumer for loading/error states.
 *
 * @param {number} pkgId - The package ID to fetch workspaces for.
 * @return {WritableAtom} Lazy atom returning WorkspacesResponse or null.
 */
export const workspacesAtom = atomFamily((_pkgId: number) =>
  lazyAtom<WorkspacesResponse>(async () => {
    const data = await request.get<WorkspacesResponse>(
      `/packages/${_pkgId}/evaluation_workspaces`
    );
    if ((data as { error?: string }).error) {
      throw new Error((data as { error: string }).error);
    }
    return data as WorkspacesResponse;
  })
);

// ---------------------------------------------------------------------------
// Calculations cache — accumulates fetched calculations by workspace ID.
// Keyed by packageId. Each entry maps workspace ID → calculations array.
// ---------------------------------------------------------------------------

/**
 * Cache atom that stores fetched calculations keyed by workspace ID.
 * Populated by fetchCalculationsBaseAtom. Read by components and derived
 * atoms (e.g. activeWorkspaceIdAtom) for rendering and context resolution.
 *
 * @param {number} pkgId - The package ID.
 * @return {Record<number, EvaluationCalculation[]>} The calculations cache.
 */
export const calculationsCacheAtom = atomFamily((_pkgId: number) =>
  atom<Record<number, EvaluationCalculation[]>>({})
);

// GET /evaluation_workspaces/:id/calculations — Fetch and cache calculations.
/**
 * Mutation atom that fetches calculations for a single workspace and merges
 * them into calculationsCacheAtom. Accepts (pkgId, wsId) as arguments.
 * Always fetches fresh data from the server.
 *
 * @param {number} pkgId - The package ID (for cache atom lookup).
 * @param {number} wsId - The workspace ID to fetch calculations for.
 * @return {Promise<CalculationsResponse>} The fetched calculations response.
 * @throws {Error} If the request fails.
 */
export const [fetchCalculationsBaseAtom, fetchCalculationsLoadableAtom] =
  mutationAtomWithLoadable<CalculationsResponse, [number, number]>(
    async (_get, set, pkgId, wsId) => {
      const data = await request.get<CalculationsResponse>(
        `/evaluation_workspaces/${wsId}/calculations?limit=10000&offset=0`
      );
      if ((data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }
      const response = data as CalculationsResponse;
      set(calculationsCacheAtom(pkgId), (prev) => ({
        ...prev,
        [wsId]: response.calculations,
      }));
      return response;
    }
  );

// ---------------------------------------------------------------------------
// Mutation atoms — explicit trigger, no cache. Used for POST endpoints and
// GET endpoints that must always fetch fresh (detail, result).
// ---------------------------------------------------------------------------

// POST /packages/:id/evaluation_workspaces — Create a new workspace.
/**
 * Mutation atom that creates a new workspace. The workspace name is auto-generated
 * by the backend as "Workspace-{id}".
 *
 * @param {number} pkgId - The package ID to create the workspace in.
 * @param {CreateWorkspaceRequest} [payload] - Optional notes for the workspace.
 * @return {Promise<CreateWorkspaceResponse>} The created workspace.
 * @throws {Error} If the request fails.
 */
export const [createWorkspaceBaseAtom, createWorkspaceLoadableAtom] =
  mutationAtomWithLoadable<
    CreateWorkspaceResponse,
    [number, CreateWorkspaceRequest?]
  >(async (_get, _set, pkgId, payload) => {
    const response = await request.post<
      CreateWorkspaceResponse,
      { error: string }
    >(
      `/packages/${pkgId}/evaluation_workspaces`,
      payload ?? {},
      new AbortController()
    );
    if (response.type === 'success') {
      return response.data;
    }
    throw new Error(
      response.type === 'error'
        ? (response.data?.error ?? 'Failed to create workspace')
        : (response.message ?? 'Failed to create workspace')
    );
  });

// PATCH /evaluation_workspaces/:id — Rename a workspace.
/**
 * Mutation atom that renames a workspace.
 *
 * @param {number} workspaceId - The workspace ID to rename.
 * @param {RenameWorkspaceRequest} payload - The new name.
 * @return {Promise<RenameWorkspaceResponse>} The updated workspace.
 * @throws {Error} If the request fails.
 */
export const [renameWorkspaceBaseAtom, renameWorkspaceLoadableAtom] =
  mutationAtomWithLoadable<
    RenameWorkspaceResponse,
    [number, RenameWorkspaceRequest]
  >(async (_get, _set, workspaceId, payload) => {
    const response = await request.patch<
      RenameWorkspaceResponse,
      { error: string }
    >(`/evaluation_workspaces/${workspaceId}`, payload, new AbortController());
    if (response.type === 'success') {
      return response.data;
    }
    throw new Error(
      response.type === 'error'
        ? (response.data?.error ?? 'Failed to rename workspace')
        : (response.message ?? 'Failed to rename workspace')
    );
  });

// POST /evaluations/calculate — Start a new calculation.
/**
 * Mutation atom that starts a new evaluation calculation.
 *
 * @param {CalculateRequest} payload - The full calculate request per §5.
 * @return {Promise<CalculateResponse>} The pending calculation with Pusher info.
 * @throws {Error} If the request fails.
 */
export const [calculateBaseAtom, calculateLoadableAtom] =
  mutationAtomWithLoadable<CalculateResponse, [CalculateRequest]>(
    async (_get, _set, payload) => {
      const response = await request.post<CalculateResponse, { error: string }>(
        '/evaluations/calculate',
        payload,
        new AbortController()
      );
      if (response.type === 'success') {
        return response.data;
      }
      throw new Error(
        response.type === 'error'
          ? (response.data?.error ?? 'Calculation failed')
          : (response.message ?? 'Calculation failed')
      );
    }
  );

// GET /api/jobs/processes/:id — Fetch current WorkflowProcess state.
// Used by use-dps-scheduling to recover from the subscribe-after-fire race:
// an empty-config DPS snapshot can finish (and Pusher emit its terminal
// event) before the FE subscribes to the channel, dropping the event.
// Reading `overall_status` after subscribe lets us detect the missed state.
export interface WorkflowProcessShowResponse {
  data?: { overall_status?: string };
}
export const [fetchProcessStateBaseAtom, fetchProcessStateLoadableAtom] =
  mutationAtomWithLoadable<WorkflowProcessShowResponse, [number]>(
    async (_get, _set, processId) => {
      const data = await request.get<WorkflowProcessShowResponse>(
        `/api/jobs/processes/${processId}`
      );
      if ((data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }
      return data as WorkflowProcessShowResponse;
    }
  );

// GET /api/jobs/processes/latest_for_calculation?calculation_id=xxx — fetch the
// selected calc's latest Aries MDB run to rehydrate on tile-switch / page load.
// Resolves to the run payload, or null when the calc has no run (the endpoint
// 404s → `request.get` returns `{ error }` → treated as "nothing to restore",
// so the prior calc's run state is cleared and nothing bleeds across calcs).
// Modeled on `fetchProcessStateBaseAtom`; consumed imperatively per selected calc.
export const [fetchAriesLatestForCalculationBaseAtom] =
  mutationAtomWithLoadable<AriesRestoredRun | null, [number]>(
    async (_get, _set, calculationId) => {
      const response = await request.get<{ data?: AriesRestoredRun }>(
        `/api/jobs/processes/latest_for_calculation?calculation_id=${calculationId}`
      );
      if ('error' in response) return null;
      return response.data ?? null;
    }
  );

// GET /evaluations/calculations/:calculation_id/downloads/aries_mdb_export/new
// — Modal defaults (INS-10553). Returns the auto-derived config the config
// modal renders (D4/D5 business defaults + the DCA-scenario forecast duration +
// the longest-PDP value that powers the too-short warning + the current default
// template name). Read-only; creates nothing. Fired on modal open.
/**
 * Mutation atom that fetches the Aries MDB export config-modal defaults.
 *
 * @param {number} calculationId - The EvaluationCalculation id (route id).
 * @return {Promise<AriesMdbExportDefaults>} The derived default config.
 * @throws {Error} If the request fails (the hook falls back to static defaults).
 */
export const [ariesMdbExportDefaultsBaseAtom] = mutationAtomWithLoadable<
  AriesMdbExportDefaults,
  [number]
>(async (_get, _set, calculationId) => {
  const data = await request.get<AriesMdbExportDefaults>(
    `/evaluations/calculations/${calculationId}/downloads/aries_mdb_export/new`
  );
  if ((data as { error?: string }).error) {
    throw new Error(
      (data as { error: string }).error || 'Failed to load export defaults'
    );
  }
  return data as AriesMdbExportDefaults;
});

// POST /evaluations/calculations/:calculation_id/downloads/aries_mdb_export
// — Kicks off an Aries MDB export (INS-10551). Creates an AriesExportProcess
// (a WorkflowProcess) and returns its Pusher channel + the 14 event
// descriptors (1 overall + 13 steps). INS-10553 supplies the edited config from
// the modal as the request body (camelCase; the BE underscores via rails-boost).
/**
 * Mutation atom that starts an Aries MDB export for a calculation.
 *
 * @param {number} calculationId - The EvaluationCalculation id (route id).
 * @param {AriesMdbExportConfig} config - The edited config from the modal.
 * @return {Promise<AriesMdbKickoffResponse>} Channel + step events to subscribe to.
 * @throws {Error} If the request fails (surfaces the server error string).
 */
// Only the base atom is consumed (the component tracks its own loading state);
// mutationAtomWithLoadable still returns the tuple, so we take the first slot.
export const [ariesMdbExportBaseAtom] = mutationAtomWithLoadable<
  AriesMdbKickoffResponse,
  [number, AriesMdbExportConfig]
>(async (_get, _set, calculationId, config) => {
  const response = await request.post<
    AriesMdbKickoffResponse,
    { error?: string }
  >(
    `/evaluations/calculations/${calculationId}/downloads/aries_mdb_export`,
    config,
    new AbortController()
  );
  if (response.type === 'success') {
    return response.data;
  }
  throw new Error(
    response.type === 'error'
      ? (response.data?.error ?? 'Failed to start Aries MDB export')
      : (response.message ?? 'Failed to start Aries MDB export')
  );
});

// POST /packages/:pkgId/development_pace_scenarios/:dpsId/schedule — Enqueue DPS scheduling job.
/**
 * Mutation atom that triggers the DPS schedule endpoint.
 * Returns a Pusher channel name immediately. The actual results arrive
 * via schedule_complete and slots_ready Pusher events.
 *
 * INS-8827 contract: the endpoint requires `scenario_id` (the BE looks
 * up the scenario's interest_type server-side and feeds it into the
 * snapshot process). The legacy `interest_type` body param has been
 * removed; sending only `interest_type` returns 422.
 *
 * @param {number} pkgId - The package ID.
 * @param {number} dpsId - The DPS ID to schedule.
 * @param {number | null | undefined} scenarioId - The selected Scenario id.
 * @param {number | null | undefined} dcaScenarioId - Optional DCA scenario filter.
 * @return {Promise<DpsScheduleEnqueueResponse>} The channel to subscribe to.
 * @throws {Error} If the request fails.
 */
export const [scheduleDpsBaseAtom, scheduleDpsLoadableAtom] =
  mutationAtomWithLoadable<
    DpsScheduleEnqueueResponse,
    [number, number, number | null | undefined, number | null | undefined]
  >(async (_get, _set, pkgId, dpsId, scenarioId, dcaScenarioId) => {
    const body: Record<string, number> = {};
    if (scenarioId) body.scenario_id = scenarioId;
    if (dcaScenarioId) body.dca_scenario_id = dcaScenarioId;
    const response = await request.post<
      DpsScheduleEnqueueResponse,
      { errors?: string[]; error?: string }
    >(
      `/packages/${pkgId}/development_pace_scenarios/${dpsId}/schedule`,
      body,
      new AbortController()
    );
    if (response.type === 'success') {
      return response.data;
    }
    const errData = response.type === 'error' ? response.data : null;
    throw new Error(
      errData?.errors?.[0] ?? errData?.error ?? 'Failed to schedule DPS'
    );
  });

// POST /evaluations/calculations/:id/save — Save an evaluation permanently.
/**
 * Mutation atom that saves an evaluation.
 *
 * @param {number} calcId - The calculation ID to save.
 * @param {SaveRequest} payload - Optional description and notes.
 * @return {Promise<SaveResponse>} The save confirmation per §7.
 * @throws {Error} If the request fails.
 */
export const [saveEvaluationBaseAtom, saveEvaluationLoadableAtom] =
  mutationAtomWithLoadable<SaveResponse, [number, SaveRequest]>(
    async (_get, _set, calcId, payload) => {
      const response = await request.post<SaveResponse, { error: string }>(
        `/evaluations/calculations/${calcId}/save`,
        payload,
        new AbortController()
      );
      if (response.type === 'success') {
        return response.data;
      }
      throw new Error(
        response.type === 'error'
          ? (response.data?.error ?? 'Failed to save evaluation')
          : (response.message ?? 'Failed to save evaluation')
      );
    }
  );

// GET /evaluations/calculations/:id — Fetch calculation detail. Always fresh (no cache).
/**
 * Mutation atom that fetches full calculation detail.
 * Uses mutationAtomWithLoadable so it never caches — re-clicking the same
 * tile always fetches fresh data from the server.
 *
 * @param {number} calcId - The calculation ID to fetch.
 * @return {Promise<CalculationDetail>} The full detail per §6.
 */
export const [
  fetchCalculationDetailBaseAtom,
  fetchCalculationDetailLoadableAtom,
] = mutationAtomWithLoadable<CalculationDetail, [number]>(
  async (_get, _set, calcId) => {
    const data = await request.get<CalculationDetail>(
      `/evaluations/calculations/${calcId}`
    );
    const record = data as Record<string, unknown>;
    if (
      !('id' in record && 'workspaceId' in record) &&
      (data as { error?: string }).error
    ) {
      throw new Error((data as { error: string }).error);
    }
    return data as CalculationDetail;
  }
);

// POST /evaluations/calculations/:id/eval_result — Fetch eval results. Always fresh.
// Accepts an aggregation_key to select which pre-computed periodicity to return.
/**
 * Mutation atom that fetches evaluation results for a calculation.
 * Uses mutationAtomWithLoadable so it never caches.
 *
 * @param {number} calcId - The numeric calculation DB id.
 * @param {string} [aggregationKey='yearly'] - The periodicity to fetch (monthly/quarterly/yearly).
 * @return {Promise<AggregatedFreeCashFlowResultResponse>} The eval_result data.
 * @throws {Error} If the request fails.
 */
export const [
  fetchCalculationResultBaseAtom,
  fetchCalculationResultLoadableAtom,
] = mutationAtomWithLoadable<
  AggregatedFreeCashFlowResultResponse,
  [number, string?]
>(async (_get, _set, calcId, aggregationKey = 'yearly') => {
  const response = await request.post<
    AggregatedFreeCashFlowResultResponse,
    { error: string }
  >(
    `/evaluations/calculations/${calcId}/eval_result`,
    { aggregation_key: aggregationKey },
    new AbortController()
  );
  if (response.type === 'success') {
    return response.data;
  }
  throw new Error(
    response.type === 'error'
      ? (response.data?.error ?? 'Failed to fetch results')
      : (response.message ?? 'Failed to fetch results')
  );
});

// ---------------------------------------------------------------------------
// Derived atoms — read-only, computed from other atoms.
// ---------------------------------------------------------------------------

/**
 * True when no workspaces exist for this package (zero-sum state).
 * Reads the loadable wrapper of workspacesAtom so it stays synchronous
 * and avoids suspension when the lazyAtom has a pending Promise.
 * Returns false while workspaces are still loading.
 *
 * @param {number} pkgId - The package ID.
 * @return {boolean} Whether the package has zero workspaces.
 */
export const isZeroSumAtom = atomFamily((_pkgId: number) => {
  const wsLoadable = loadable(workspacesAtom(_pkgId));
  return atom((get) => {
    const state = get(wsLoadable);
    if (state.state !== 'hasData' || !state.data) return false;
    return state.data.workspaces.length === 0;
  });
});

/**
 * Derives the workspace ID that owns the currently selected calculation.
 * This is the "active" workspace for context operations (calculate, save,
 * rename) — distinct from selectedWorkspaceId which tracks accordion
 * expansion for browsing and data fetching.
 * Returns null when no calculation is selected.
 *
 * @param {number} pkgId - The package ID.
 * @return {number | null} The workspace ID owning the selected calculation.
 */
export const activeWorkspaceIdAtom = atomFamily((_pkgId: number) =>
  atom((get) => {
    const selectedCalcId = get(selectedCalculationIdAtom(_pkgId));
    if (selectedCalcId === null) return null;
    const cache = get(calculationsCacheAtom(_pkgId));
    for (const [wsId, calcs] of Object.entries(cache)) {
      if (calcs.some((c) => c.id === selectedCalcId)) return Number(wsId);
    }
    return null;
  })
);

/**
 * Assembles the current calculation config from all working copy atoms.
 * Always returns a config object reflecting the true state of the inputs.
 * eval2Id is null when no Eval 2.0 is selected.
 *
 * @param {number} pkgId - The package ID.
 * @return {CalculationConfig} The current config snapshot.
 */
export const currentConfigAtom = atomFamily((_pkgId: number) =>
  atom<CalculationConfig>((get) => ({
    evalMode: get(selectedEvalModeAtom(_pkgId)),
    evaluationCaseId: get(selectedEvaluationCaseIdAtom(_pkgId)),
    scenarioId: get(selectedScenarioIdAtom(_pkgId)),
    eval2Id: get(selectedEval2IdAtom(_pkgId)),
    dpsId: get(selectedDpsIdAtom(_pkgId)),
    drsId: get(selectedDrsIdAtom(_pkgId)),
    differentialScenarioId: get(selectedDifferentialScenarioIdAtom(_pkgId)),
    taxScenarioId: get(selectedTaxScenarioIdAtom(_pkgId)),
    expenseScenarioId: get(selectedExpenseScenarioIdAtom(_pkgId)),
    priceDeckId: get(selectedPriceDeckIdAtom(_pkgId)),
    dcaScenarioId: get(selectedDcaScenarioIdAtom(_pkgId)),
    effectiveDate: get(workingEffectiveDateAtom(_pkgId)) ?? '',
    measurementType: get(workingMeasurementTypeAtom(_pkgId)),
    slotVolumeSource: get(workingSlotVolumeSourceAtom(_pkgId)),
    slotAfeSource: get(workingSlotAfeSourceAtom(_pkgId)),
    slotVolumeConfigurations: get(workingSlotVolumeConfigurationsAtom(_pkgId)),
    slotAfeConfigurations: get(workingSlotAfeConfigurationsAtom(_pkgId)),
    slotCostDateConfigurations: get(
      workingSlotCostDateConfigurationsAtom(_pkgId)
    ),
    slotDateConfigurations: get(workingSlotDateConfigurationsAtom(_pkgId)),
    intervalsPreEffectiveDate: get(workingIntervalsPreAtom(_pkgId)),
    intervalsPostEffectiveDate: get(workingIntervalsPostAtom(_pkgId)),
    absoluteMinDate: get(workingAbsoluteMinDateAtom(_pkgId)),
    absoluteMaxDate: get(workingAbsoluteMaxDateAtom(_pkgId)),
    dateRangeFilterType: get(workingDateRangeFilterTypeAtom(_pkgId)),
  }))
);

/**
 * True when the current config has all required fields for a calculation.
 *
 * @param {number} pkgId - The package ID.
 * @return {boolean} Whether the config is valid for calculation.
 */
export const isValidConfigAtom = atomFamily((_pkgId: number) =>
  atom((get) => {
    const config = get(currentConfigAtom(_pkgId));
    const dpsSchedule = get(dpsScheduleResultAtom(_pkgId));

    // Case mode: DPS scheduling handled by CaseResolver at runtime — only
    // need a scenario_id (for global assumptions) + case ID + effective date.
    if (config.evalMode === 'case') {
      const caseValid =
        config.scenarioId !== null &&
        config.evaluationCaseId !== null &&
        config.effectiveDate !== '';
      if (!caseValid) return false;

      if (config.dateRangeFilterType === 'relative') {
        return (
          config.intervalsPreEffectiveDate > 0 &&
          config.intervalsPostEffectiveDate > 0
        );
      }
      return config.absoluteMinDate !== '' && config.absoluteMaxDate !== '';
    }

    // Legacy mode: scenario_id + DPS fully scheduled + effective date.
    const dpsStatus = get(dpsScheduleStatusAtom(_pkgId));
    const dpsReady =
      config.dpsId !== null &&
      dpsSchedule !== null &&
      dpsStatus === 'ready' &&
      !!dpsSchedule.slotsBlobPath &&
      !!dpsSchedule.scheduledSlotsBlobPath;
    const baseValid =
      config.scenarioId !== null && dpsReady && config.effectiveDate !== '';

    if (!baseValid) return false;

    if (config.dateRangeFilterType === 'relative') {
      return (
        config.intervalsPreEffectiveDate > 0 &&
        config.intervalsPostEffectiveDate > 0
      );
    }

    // absolute
    return (
      config.absoluteMinDate !== null &&
      config.absoluteMinDate !== '' &&
      config.absoluteMaxDate !== null &&
      config.absoluteMaxDate !== ''
    );
  })
);

/**
 * Picks only the fields that are sent to POST /evaluations/calculate.
 * Other settings (measurementType, date range filters, etc.) are
 * client-only display preferences and should not trigger recalculation.
 *
 * @param {CalculationConfig} cfg - The full config snapshot.
 * @return {object} The subset of fields sent to the backend.
 */
const pickCalculateFields = (cfg: CalculationConfig) => ({
  evalMode: cfg.evalMode,
  evaluationCaseId: cfg.evaluationCaseId,
  scenarioId: cfg.scenarioId,
  eval2Id: cfg.eval2Id,
  dpsId: cfg.dpsId,
  priceDeckId: cfg.priceDeckId,
  dcaScenarioId: cfg.dcaScenarioId,
  effectiveDate: cfg.effectiveDate,
  slotVolumeSource: cfg.slotVolumeSource,
  slotAfeSource: cfg.slotAfeSource,
  slotVolumeConfigurations: cfg.slotVolumeConfigurations,
  slotAfeConfigurations: cfg.slotAfeConfigurations,
  slotCostDateConfigurations: cfg.slotCostDateConfigurations,
  slotDateConfigurations: cfg.slotDateConfigurations,
});

/**
 * True when the calculate-relevant fields of the current config differ
 * from the last applied config. Only compares the fields actually sent
 * to POST /evaluations/calculate. Also true when no calculation has
 * been run yet (appliedConfig is null).
 *
 * @param {number} pkgId - The package ID.
 * @return {boolean} Whether calculate-relevant config changes exist.
 */
export const isDirtyAtom = atomFamily((_pkgId: number) =>
  atom((get) => {
    const current = get(currentConfigAtom(_pkgId));
    const applied = get(appliedConfigAtom(_pkgId));
    if (!applied)
      return (
        current.scenarioId !== null ||
        current.eval2Id !== null ||
        current.dpsId !== null ||
        current.evaluationCaseId !== null
      );

    return !isEqual(pickCalculateFields(current), pickCalculateFields(applied));
  })
);

/**
 * Slot IDs whose AFE override differs from the last calculation that was run
 * (the appliedConfig baseline) — i.e. AFE overrides not yet realized in an eval.
 * Drives the "changed since last run" blue cell highlight and the pre-export
 * warning. Covers added, removed, and changed overrides. Empties automatically
 * once a run completes and setAppliedConfig rebaselines, so the highlight clears.
 *
 * @param {number} pkgId - The package ID.
 * @return {Set<string>} Slot IDs with AFE overrides not yet run in an eval.
 */
export const afeChangedSinceLastRunAtom = atomFamily((_pkgId: number) =>
  atom<Set<string>>((get) => {
    const current = get(workingSlotAfeConfigurationsAtom(_pkgId));
    const applied = get(appliedConfigAtom(_pkgId))?.slotAfeConfigurations ?? {};
    const changed = new Set<string>();
    new Set([...Object.keys(current), ...Object.keys(applied)]).forEach(
      (slotId) => {
        if (!isEqual(current[slotId], applied[slotId])) changed.add(slotId);
      }
    );
    return changed;
  })
);

/**
 * Write-only atom that hydrates all working copy atoms from a calculation
 * detail and snapshots appliedConfig. Builds the snapshot explicitly from
 * the hydrated values (wc.*) and current client-only prefs (via get()),
 * mirroring exactly what currentConfigAtom computes. This avoids relying
 * on derived-atom cache invalidation during the write transaction.
 *
 * @param {number} pkgId - The package ID.
 * @param {WorkingCopyValues} wc - Extracted working copy values from detail.
 */
export const hydrateWorkingCopyAtom = atomFamily((_pkgId: number) =>
  atom(null, (_get, set, wc: WorkingCopyValues) => {
    // Server-persisted settings (from /evaluations/calculate inputs)
    set(selectedEvalModeAtom(_pkgId), wc.evalMode ?? 'legacy');
    set(selectedEvaluationCaseIdAtom(_pkgId), wc.evaluationCaseId ?? null);
    set(selectedPriceDeckIdAtom(_pkgId), wc.priceDeckId);
    set(selectedDcaScenarioIdAtom(_pkgId), wc.dcaScenarioId);
    set(workingEffectiveDateAtom(_pkgId), wc.effectiveDate);
    set(
      workingSlotVolumeConfigurationsAtom(_pkgId),
      wc.slotVolumeConfigurations
    );
    set(workingSlotAfeConfigurationsAtom(_pkgId), wc.slotAfeConfigurations);
    set(
      workingSlotCostDateConfigurationsAtom(_pkgId),
      wc.slotCostDateConfigurations
    );
    set(
      workingSlotDateConfigurationsAtom(_pkgId),
      wc.slotDateConfigurations ?? {}
    );
    set(selectedEval2IdAtom(_pkgId), wc.eval2Id);
    set(selectedScenarioIdAtom(_pkgId), wc.scenarioId);
    set(selectedDpsIdAtom(_pkgId), wc.dpsId ?? null);
    set(selectedDrsIdAtom(_pkgId), wc.drsId ?? null);
    set(
      selectedDifferentialScenarioIdAtom(_pkgId),
      wc.differentialScenarioId ?? null
    );
    set(selectedTaxScenarioIdAtom(_pkgId), wc.taxScenarioId ?? null);
    set(selectedExpenseScenarioIdAtom(_pkgId), wc.expenseScenarioId ?? null);

    // Reset non-persisted settings to defaults
    set(workingIntervalAtom(_pkgId), 'year');
    set(workingMeasurementTypeAtom(_pkgId), 'net');
    set(workingSlotVolumeSourceAtom(_pkgId), wc.slotVolumeSource);
    set(workingSlotAfeSourceAtom(_pkgId), wc.slotAfeSource);
    set(workingIntervalsPreAtom(_pkgId), 2);
    set(workingIntervalsPostAtom(_pkgId), 10);
    set(workingAbsoluteMinDateAtom(_pkgId), null);
    set(workingAbsoluteMaxDateAtom(_pkgId), null);
    set(workingDateRangeFilterTypeAtom(_pkgId), 'relative');

    // Build appliedConfig explicitly, mirroring currentConfigAtom's shape.
    set(appliedConfigAtom(_pkgId), {
      evalMode: wc.evalMode ?? ('legacy' as const),
      evaluationCaseId: wc.evaluationCaseId ?? null,
      scenarioId: wc.scenarioId,
      eval2Id: wc.eval2Id,
      dpsId: wc.dpsId ?? null,
      drsId: wc.drsId ?? null,
      differentialScenarioId: wc.differentialScenarioId ?? null,
      taxScenarioId: wc.taxScenarioId ?? null,
      expenseScenarioId: wc.expenseScenarioId ?? null,
      priceDeckId: wc.priceDeckId,
      dcaScenarioId: wc.dcaScenarioId,
      effectiveDate: wc.effectiveDate ?? '',
      measurementType: 'net',
      slotVolumeSource: wc.slotVolumeSource,
      slotAfeSource: wc.slotAfeSource,
      slotVolumeConfigurations: wc.slotVolumeConfigurations,
      slotAfeConfigurations: wc.slotAfeConfigurations,
      slotCostDateConfigurations: wc.slotCostDateConfigurations,
      slotDateConfigurations: wc.slotDateConfigurations ?? {},
      intervalsPreEffectiveDate: 2,
      intervalsPostEffectiveDate: 10,
      absoluteMinDate: null,
      absoluteMaxDate: null,
      dateRangeFilterType: 'relative',
    });
  })
);

// ---------------------------------------------------------------------------
// INS-8866 Executive Summary — progressive panel state.
//
// Pre-calc, in-flight Pusher events, and post-calc eval_result all merge into
// the same object so the panel can render whatever has arrived so far.
// Missing keys = "not available yet", not zero.
// ---------------------------------------------------------------------------

export const executiveSummaryAtom = atomFamily((_pkgId: number) =>
  atom<ExecutiveSummary>({})
);

// Write-only setter that merges a partial ExecutiveSummary onto the current
// state. Used by the Pusher section handler and by the post-calc eval_result
// effect. Resetting back to {} is done with a plain set on the base atom.
export const mergeExecutiveSummaryAtom = atomFamily((pkgId: number) =>
  atom(null, (get, set, partial: ExecutiveSummary) => {
    const current = get(executiveSummaryAtom(pkgId));
    set(executiveSummaryAtom(pkgId), { ...current, ...partial });
  })
);

// POST /packages/:id/executive_summary — INS-8875 pre-calc endpoint.
// Flat response, no body, no DPS parameter, never returns pricing keys.
export const [
  fetchPreCalcExecutiveSummaryBaseAtom,
  fetchPreCalcExecutiveSummaryLoadableAtom,
] = mutationAtomWithLoadable<ExecutiveSummary, [number]>(
  async (_get, _set, pkgId) => {
    const response = await request.post<ExecutiveSummary, { error: string }>(
      `/packages/${pkgId}/executive_summary`,
      {},
      new AbortController()
    );
    if (response.type === 'success') {
      return response.data;
    }
    throw new Error(
      response.type === 'error'
        ? (response.data?.error ?? 'Failed to fetch executive summary')
        : (response.message ?? 'Failed to fetch executive summary')
    );
  }
);

// ---------------------------------------------------------------------------
// Export presets — GET /eval/export_presets
// ---------------------------------------------------------------------------

/**
 * Named column ordering presets for all Eval 3 parquet-based export types.
 * Fetched once when the Eval 3 workspace mounts; each export table reads its slice.
 */
export const [evalExportPresetsAtom, evalExportPresetsLoadableAtom] =
  atomWithLoadable(async () => {
    const response = await request.get<EvalExportPresets>(
      '/eval/export_presets'
    );
    if ('error' in response) throw new Error(response.error);
    return response;
  });

// ── Evaluation Case selection (INS-9947) ──────────────────────────────

/** Whether the user is in "Evaluation Case" or "Eval 2.0 Base" mode. */
export type EvalMode = 'case' | 'legacy';

export const selectedEvalModeAtom = atomFamily((_id: number) =>
  atom<EvalMode>('legacy')
);

/** The selected Evaluation Case ID. */
export const selectedEvaluationCaseIdAtom = atomFamily((_id: number) =>
  atom<number | null>(null)
);

/** Evaluation Case list for the ComboBox dropdown. */
export interface EvaluationCaseListItem {
  id: number;
  name: string;
  evaluationType: string;
  scope: string | null;
}

export const [
  fetchEvaluationCaseListAtom,
  fetchEvaluationCaseListLoadableAtom,
] = mutationAtomWithLoadable<EvaluationCaseListItem[], [number | null]>(
  async (_get, _set, includeId: number | null) => {
    const controller = new AbortController();
    const res = await request.post<
      { results: EvaluationCaseListItem[] },
      unknown
    >(
      '/queries/evaluation_cases',
      {
        query: {
          sortAttribute: 'updated_at',
          sortDirection: 'DESC',
          pageSize: 500,
          ...(includeId ? { includeId } : {}),
        },
      },
      controller
    );
    if (res.type === 'success') return res.data.results ?? [];
    throw new Error('Failed to fetch evaluation cases');
  }
);

/**
 * The selected Evaluation Case as the workspace reads it: the eval page's EvaluationCaseDetail (one
 * contract and one query for GET /evaluation_cases/:id, useEvaluationCaseDetail), narrowed to the
 * fields the domain chips and the DCA rule read.
 */
export type EvaluationCaseDetail = Pick<
  Eval3EvaluationCaseDetail,
  'id' | 'name' | 'evaluationType' | 'scope' | 'priceDeckId' | 'priceDeckName'
> &
  Partial<
    Pick<
      Eval3EvaluationCaseDetail,
      'dcaScenarioId' | 'scenarioDomains' | 'archivedDomains'
    >
  >;
